<script setup lang="ts">
import UiTransitionExpand from '@boats/ui/components/UiTransitionExpand.vue'

type Props = {
  title: string
  description?: string | null
  price: string
  /** Pre-formatted reference price; only set when the category is discounted. */
  wasPrice?: string | null
  /** Money saved per ticket (was − now); drives the "SAVE …" badge. */
  saved?: number | null
  class?: string
}

const props = defineProps<Props>()

const isDiscounted = computed(() => !!props.wasPrice && !!props.saved)

const classes = computed(() => ({
  'app-ticket': true,
  [`${props.class}`]: !!props.class,
}))

const open = ref(false)
</script>

<template>
  <AppPaper :class="classes">
    <div class="flex">
      <div class="app-ticket__column">
        <h4 class="mb-3">{{ title }}</h4>

        <AppButton
          v-if="description"
          variant="text"
          class="mt-auto"
          @click="open = !open"
        >
          {{ open ? $t('string.Show-less') : $t('string.Show-more') }}

          <template #icon-end-circle>
            <IconMinus v-if="open" />
            <IconPlus v-else />
          </template>
        </AppButton>
      </div>

      <div class="app-ticket__column">
        <div class="app-ticket__price my-auto">
          <span class="app-ticket__now h4 mb-0">{{ price }}</span>
          <span v-if="isDiscounted" class="app-ticket__was">{{ wasPrice }}</span>
        </div>

        <PriceDropBadge
          v-if="isDiscounted"
          :saved="saved as number"
          class="app-ticket__save"
        />

        <slot></slot>
      </div>
    </div>

    <UiTransitionExpand>
      <div v-show="open" class="app-ticket__info">
        <div v-html="description"></div>
      </div>
    </UiTransitionExpand>
  </AppPaper>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-ticket {
}

.app-ticket__price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.375rem;
  text-align: center;
}

.app-ticket__was {
  font-family: $font-barlow;
  font-size: 0.875rem;
  line-height: 1.2;
  color: $gray-300;
  text-decoration: line-through;
}

.app-ticket__save {
  margin-top: 0.5rem;
}

.app-ticket__column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:first-child {
    flex: 1 1 0%;
    align-items: flex-start;
    padding: 0.75rem;
  }

  &:last-child {
    align-items: center;
    min-width: 7.5rem;
    padding: 0.75rem 0.25rem 0.25rem;
    border-left: 1px solid $gray-200;

    .app-button {
      margin-top: 0.75rem;
    }
  }

  @include media-breakpoint-up(md) {
    &:first-child {
      padding: 1.5rem;
    }

    &:last-child {
      min-width: 10rem;
      padding: 1.5rem 0.5rem 0.5rem;

      .app-button {
        margin-top: 1.5rem;
      }
    }
  }
}

.app-ticket__info {
  & > div {
    padding: 1.5rem 0.75rem;
    border-top: 1px solid $gray-200;
  }

  @include media-breakpoint-up(md) {
    & > div {
      padding: 1.5rem;
    }
  }
}
</style>
